import { useQuery } from '@tanstack/react-query'
import { supportedChains } from 'constants/chains'
import { AddressZero } from 'constants/index'
import { useMemo } from 'react'
import { useAddErrorPopup } from 'state/application/hooks'
import {
  Chain,
  Collection,
  CollectionType,
  CollectionVerificationStatus
} from 'types/nft'
import { convertChainToChainId } from 'utils/chain'
import { fetchStaticJson } from 'utils/staticData'
import { getAddress, isAddress } from 'viem'
import { erc721ABI, useContractReads } from 'wagmi'

const ERC165_ABI = [
  {
    inputs: [{ name: 'interfaceId', type: 'bytes4' }],
    name: 'supportsInterface',
    outputs: [{ type: 'bool' }],
    stateMutability: 'view',
    type: 'function'
  }
] as const

const ERC1155_INTERFACE_ID = '0xd9b67a26'

const selectSupportedChains = (collections: Collection[]): Collection[] =>
  collections.filter((collection) =>
    supportedChains.some((chain) => chain.id === collection.chainId)
  )

export const useCollectionRegistry = () => {
  const addErrorPopup = useAddErrorPopup()
  return useQuery(
    ['collections'],
    () => fetchStaticJson<Collection[]>('collections.json'),
    {
      onError: (error) => {
        if (error instanceof Error) {
          addErrorPopup(error.message)
        }
      },
      select: selectSupportedChains,
      staleTime: Infinity
    }
  )
}

export const findCollection = (
  collections: Collection[],
  addressOrSlug: string,
  chain: Chain
): Collection | undefined => {
  const key = addressOrSlug.toLowerCase()
  return collections.find(
    (collection) =>
      collection.chain === chain &&
      (collection.address === key || collection.slugName?.toLowerCase() === key)
  )
}

const useOnChainCollection = (
  address: string,
  chain: Chain,
  enabled: boolean
): { data: Collection | undefined; isLoading: boolean } => {
  const chainId = convertChainToChainId(chain)
  const isValidAddress = isAddress(address)
  const isEnabled = enabled && isValidAddress
  const contract = {
    address: isValidAddress ? getAddress(address) : AddressZero,
    chainId
  } as const

  const { data, isLoading } = useContractReads({
    allowFailure: true,
    contracts: [
      { ...contract, abi: erc721ABI, functionName: 'name' },
      { ...contract, abi: erc721ABI, functionName: 'symbol' },
      { ...contract, abi: erc721ABI, functionName: 'totalSupply' },
      {
        ...contract,
        abi: ERC165_ABI,
        args: [ERC1155_INTERFACE_ID],
        functionName: 'supportsInterface'
      }
    ],
    enabled: isEnabled
  })

  const collection = useMemo((): Collection | undefined => {
    if (!isEnabled || !data) {
      return undefined
    }
    const [name, symbol, totalSupply, supportsErc1155] = data
    if (data.every((read) => read.status === 'failure')) {
      return undefined
    }
    return {
      address: address.toLowerCase(),
      chain,
      chainId,
      name: name.result ?? '',
      numItems:
        totalSupply.result === undefined
          ? undefined
          : Number(totalSupply.result),
      symbol: symbol.result ?? '',
      type: supportsErc1155.result
        ? CollectionType.ERC1155
        : CollectionType.ERC721,
      verified: CollectionVerificationStatus.Unverified
    }
  }, [address, chain, chainId, data, isEnabled])

  return { data: collection, isLoading: isEnabled && isLoading }
}

export const useCollection = (
  addressOrSlug: string,
  chain: Chain
): { data: Collection | undefined; isLoading: boolean } => {
  const { data: collections, isLoading: isLoadingRegistry } =
    useCollectionRegistry()

  const registryCollection = useMemo(
    () =>
      collections
        ? findCollection(collections, addressOrSlug, chain)
        : undefined,
    [addressOrSlug, chain, collections]
  )

  const onChainCollection = useOnChainCollection(
    addressOrSlug,
    chain,
    !isLoadingRegistry && !registryCollection
  )

  return {
    data: registryCollection ?? onChainCollection.data,
    isLoading: isLoadingRegistry || onChainCollection.isLoading
  }
}
